/* File: PhotosPickerController.jsx
 * Path: ananunadm/src/components/main/PhotosPickerController.jsx
 * Description: Headless controller for the Google Photos Picker flow. Listens for the
 * "apit-open-photos-picker" event (dispatched by GoogleSyncMenu), opens Google's hosted
 * picker in a popup, polls the session until the user finishes picking, then imports the
 * picked items into APIT Media. No full-library access — only what the user explicitly picks.
 */
import { useEffect, useRef } from "react";
import { notifications } from "@mantine/notifications";
import axiosApi from "@/helpers/api";
import config from "@/config";

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 150; // ~5 minutes

export default function PhotosPickerController() {
	const pollTimerRef = useRef(null);
	const popupRef = useRef(null);

	useEffect(() => {
		function stopPolling() {
			if (pollTimerRef.current) {
				clearTimeout(pollTimerRef.current);
				pollTimerRef.current = null;
			}
		}

		async function importSession(sessionId) {
			try {
				const response = await axiosApi.post(
					`${config.api.googleServices}/photos/picker-session/${sessionId}/import`
				);
				const imported = response.data?.imported || 0;
				window.dispatchEvent(new CustomEvent("apit-media-changed"));
				notifications.show({
					color: imported > 0 ? "green" : "yellow",
					message: imported > 0
						? `Imported ${imported} photo(s) from Google Photos`
						: "No new photos were picked"
				});
			} catch (error) {
				notifications.show({
					color: "red",
					message: error?.response?.data?.error || "Could not import picked photos"
				});
			}
		}

		function pollSession(sessionId, attempt = 0) {
			stopPolling();
			if (attempt >= MAX_POLL_ATTEMPTS) {
				notifications.show({ color: "yellow", message: "Google Photos picker timed out" });
				return;
			}
			pollTimerRef.current = setTimeout(async () => {
				try {
					const response = await axiosApi.get(
						`${config.api.googleServices}/photos/picker-session/${sessionId}`
					);
					if (response.data?.mediaItemsSet) {
						popupRef.current?.close?.();
						await importSession(sessionId);
						return;
					}
					if (popupRef.current?.closed) {
						return; // user closed the picker without finishing
					}
					pollSession(sessionId, attempt + 1);
				} catch (error) {
					notifications.show({
						color: "red",
						message: error?.response?.data?.error || "Google Photos picker session failed"
					});
				}
			}, POLL_INTERVAL_MS);
		}

		async function onOpenPicker() {
			try {
				const response = await axiosApi.post(`${config.api.googleServices}/photos/picker-session`);
				const { id, pickerUri } = response.data || {};
				if (!id || !pickerUri) throw new Error("Google did not return a picker session");
				popupRef.current = window.open(
					pickerUri,
					"apit-google-photos-picker",
					"width=1024,height=768,menubar=no,toolbar=no,location=yes,status=no"
				);
				if (!popupRef.current) {
					window.location.href = pickerUri;
					return;
				}
				pollSession(id);
			} catch (error) {
				notifications.show({
					color: "red",
					message: error?.response?.data?.error || "Could not open Google Photos picker"
				});
			}
		}

		window.addEventListener("apit-open-photos-picker", onOpenPicker);
		return () => {
			window.removeEventListener("apit-open-photos-picker", onOpenPicker);
			stopPolling();
		};
	}, []);

	return null;
}
